import InputField from "components/fields/InputField";
import Modal from "components/modal";
import Button from "components/button";

import React, {  useMemo, useState } from "react";
import { useForm, Controller } from "react-hook-form";
import { createUsers } from "store/userSlice";
import { useDispatch, useSelector } from "react-redux";
import LoaderButton from "components/button/LoaderButton";
import SelectField from "components/fields/SelectField";

function AddUser({ refreshUserList }) {
  const dispatch = useDispatch();
  const { createLoading, roleData } = useSelector((state) => state.users);

  const [isOpen, setIsOpen] = useState(false);
  const openModal = () => setIsOpen(true);
  const closeModal = () => {
    reset();
    setIsOpen(false);
  };

  const activeRolesOptions = useMemo(() => {
    if (!Array.isArray(roleData) || roleData.length === 0) {
      return [];
    }

    return roleData
      .filter((role) => role.isActive)
      .map((role) => ({
        value: role.id,
        label: role.name,
      }));
  }, [roleData]);

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: {
      firstname: "",
      lastname: "",
      email: "",
      mobile: "",
      password: "",
      roleId: "",
    },
    mode: "onBlur",
  });

  const onSubmit = async (data) => {
    try {
      const response = await dispatch(createUsers(data));

      if (response?.payload?.statuscode === 201) {
        refreshUserList();
        closeModal();
        reset();
      } else {
        console.error(
          "Create failed:",
          response?.payload?.msg || "Unknown error"
        );
      }
    } catch (error) {
      console.error("Create failed:", error);
    }
  };

  return (
    <div>
      <Button onClick={openModal}>Add User</Button>

      <Modal isOpen={isOpen} onClose={closeModal} className="max-w-4xl">
        <h2 className="border-b py-3 pl-2 text-xl font-bold text-navy-700 dark:text-white">
          Add User
        </h2>

        <div className="my-5 w-full max-w-4xl flex-col items-center lg:pl-0">
          <form
            onSubmit={handleSubmit(onSubmit)}
            className="mx-auto grid w-full grid-cols-1 gap-4"
            noValidate
          >
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <InputField
                id="firstname"
                variant="auth"
                label="Firstname*"
                placeholder="Enter firstname"
                type="text"
                error={errors.firstname}
                {...register("firstname", {
                  required: "Firstname is required",
                })}
              />

              <InputField
                id="lastname"
                variant="auth"
                label="Lastname*"
                placeholder="Enter lastname"
                type="text"
                error={errors.lastname}
                {...register("lastname", { required: "Lastname is required" })}
              />
              <Controller
                name="roleId"
                control={control}
                rules={{ required: "User type is required" }}
                render={({ field }) => (
                  <SelectField
                    label="Role*"
                    options={activeRolesOptions}
                    placeholder="Select user role"
                    value={
                      field.value
                        ? activeRolesOptions.find(
                            (opt) => opt.value === field.value
                          )
                        : null
                    }
                    onChange={(selected) => field.onChange(selected?.value)}
                    error={errors.roleId}
                  />
                )}
              />

              <InputField
                id="email"
                variant="auth"
                label="Email*"
                placeholder="mail@simmmple.com"
                type="email"
                autoComplete="email"
                error={errors.email}
                {...register("email", {
                  required: "Email is required",
                  pattern: {
                    value: /^\S+@\S+$/i,
                    message: "Invalid email address",
                  },
                })}
              />

              <InputField
                id="mobile"
                variant="auth"
                label="Mobile*"
                placeholder="9797979797"
                type="tel"
                autoComplete="tel"
                maxLength={10}
                error={errors.mobile}
                {...register("mobile", {
                  required: "Mobile number is required",
                  pattern: {
                    value: /^[0-9]{10}$/,
                    message: "Enter a valid 10-digit number",
                  },
                })}
                onInput={(e) => {
                  e.currentTarget.value = e.currentTarget.value.replace(
                    /[^0-9]/g,
                    ""
                  );
                }}
              />

              <InputField
                id="password"
                variant="auth"
                label="Password*"
                placeholder="Min. 8 characters"
                type="password"
                autoComplete="new-password"
                error={errors.password}
                {...register("password", {
                  required: "Password is required",
                  minLength: {
                    value: 8,
                    message: "At least 8 characters required",
                  },
                })}
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={closeModal}>
                Cancel
              </Button>

              <LoaderButton type="submit" isLoading={createLoading}>
                Add User
              </LoaderButton>
            </div>
          </form>
        </div>
      </Modal>
    </div>
  );
}

export default AddUser;
